import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Circle, Polygon, Marker, useMapEvents, useMap } from 'react-leaflet'
import { Icon, LatLng, LeafletMouseEvent } from 'leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Button } from '../ui'
import { Circle as CircleIcon, Hexagon, Trash2, Navigation } from 'lucide-react'

// Fix for default markers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (Icon.Default.prototype as any)._getIconUrl
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
})

export interface Geofence {
  id: string
  type: 'CIRCLE' | 'POLYGON'
  name: string
  // For circles
  center?: { lat: number; lng: number }
  radius?: number
  // For polygons
  points?: { lat: number; lng: number }[]
}

interface GeofenceMapEditorProps {
  location: { lat: number; lng: number }
  initialCenter?: { lat: number; lng: number }  // Optional - defaults to location if not provided
  geofences: Geofence[]
  onGeofencesChange: (geofences: Geofence[]) => void
  onLocationChange?: (location: { lat: number; lng: number }) => void
  height?: string
}

// Component to handle map interactions
function MapInteraction({ 
  mode, 
  onAddPoint, 
  onMapClick,
  map,
  currentCircle
}: { 
  mode: 'view' | 'circle' | 'polygon'
  onAddPoint: (latlng: LatLng) => void
  onMapClick: (latlng: LatLng) => void
  map: L.Map
  currentPolygonPoints: { lat: number; lng: number }[]
  currentCircle: { center: { lat: number; lng: number } } | null
}) {
  useMapEvents({
    click: (e: LeafletMouseEvent) => {
      if (mode === 'circle') {
        onMapClick(e.latlng)
      } else if (mode === 'polygon') {
        onAddPoint(e.latlng)
      }
    }
  })

  // Change cursor based on mode
  useEffect(() => {
    const container = map.getContainer()
    if (mode === 'circle' || mode === 'polygon') {
      container.style.cursor = 'crosshair'
    } else {
      container.style.cursor = ''
    }
    return () => {
      container.style.cursor = ''
    }
  }, [mode, map])

  // Auto-add circle at map center only when first switching to circle mode
  useEffect(() => {
    if (mode === 'circle' && !currentCircle && map) {
      const center = map.getCenter()
      onMapClick(center)
    }
  }, [mode, map, currentCircle, onMapClick])

  return null
}

// Component to resize map when container changes
function MapResizer() {
  const map = useMap()
  
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize()
    }, 100)
  }, [map])
  
  return null
}

// Component to adjust map bounds to show all geofences
function MapBoundsAdjuster({ geofences }: { geofences: Geofence[] }) {
  const map = useMap()
  
  useEffect(() => {
    if (!map || !geofences || geofences.length === 0) return
    
    const bounds = L.latLngBounds([])
    
    geofences.forEach(geofence => {
      if (geofence.type === 'CIRCLE' && geofence.center) {
        // For circles, calculate bounds manually using the radius
        const center = L.latLng(geofence.center.lat, geofence.center.lng)
        const radius = geofence.radius || 100
        
        // Calculate bounds by adding points at the circle's extremities
        // Convert radius from meters to approximate degrees
        const latOffset = (radius / 111000) // ~111km per degree latitude
        const lngOffset = (radius / (111000 * Math.cos(center.lat * Math.PI / 180))) // Adjust for latitude
        
        // Add the four cardinal points of the circle
        bounds.extend([center.lat + latOffset, center.lng])
        bounds.extend([center.lat - latOffset, center.lng])
        bounds.extend([center.lat, center.lng + lngOffset])
        bounds.extend([center.lat, center.lng - lngOffset])
      } else if (geofence.type === 'POLYGON' && geofence.points) {
        // For polygons, add all points
        geofence.points.forEach(point => {
          bounds.extend([point.lat, point.lng])
        })
      }
    })
    
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [map, geofences])
  
  return null
}

// Component to handle location centering
function LocationControl({ onLocationFound }: { onLocationFound?: (lat: number, lng: number) => void }) {
  const map = useMap()
  const [loading, setLoading] = useState(false)
  
  const centerOnCurrentLocation = () => {
    setLoading(true)
    
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          map.setView([latitude, longitude], 16)
          if (onLocationFound) {
            onLocationFound(latitude, longitude)
          }
          setLoading(false)
        },
        (error) => {
          console.error('Error getting location:', error)
          alert('Unable to get your location. Please check your browser permissions.')
          setLoading(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    } else {
      alert('Geolocation is not supported by your browser')
      setLoading(false)
    }
  }
  
  return (
    <div className="leaflet-top leaflet-right">
      <div className="leaflet-control leaflet-bar" style={{ marginTop: '10px', marginRight: '10px' }}>
        <button
          onClick={centerOnCurrentLocation}
          disabled={loading}
          className="bg-white hover:bg-gray-100 rounded shadow-sm border border-gray-300 p-2 cursor-pointer disabled:opacity-50"
          title="Go to my location"
          style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
          ) : (
            <Navigation className="w-4 h-4 text-gray-700" />
          )}
        </button>
      </div>
    </div>
  )
}

export function GeofenceMapEditor({ location, initialCenter, geofences, onGeofencesChange, onLocationChange, height = '400px' }: GeofenceMapEditorProps) {
  const [map, setMap] = useState<L.Map | null>(null)
  const [mode, setMode] = useState<'view' | 'circle' | 'polygon'>('view')
  const [currentCircle, setCurrentCircle] = useState<{ center: { lat: number; lng: number } } | null>(null)
  const [currentPolygonPoints, setCurrentPolygonPoints] = useState<{ lat: number; lng: number }[]>([])
  const [selectedGeofence, setSelectedGeofence] = useState<string | null>(null)
  const [geofenceName, setGeofenceName] = useState('')
  const [circleRadius, setCircleRadius] = useState(100)
  // Validate coordinates before using them
  const validLocation = {
    lat: typeof location.lat === 'number' && !isNaN(location.lat) ? location.lat : 40.7128,
    lng: typeof location.lng === 'number' && !isNaN(location.lng) ? location.lng : -74.0060
  }
  const [mapCenter, setMapCenter] = useState(initialCenter || validLocation)

  const generateId = () => `geofence-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  // Update map center when initialCenter or location prop changes
  useEffect(() => {
    const validCenter = initialCenter || validLocation
    setMapCenter({
      lat: typeof validCenter.lat === 'number' && !isNaN(validCenter.lat) ? validCenter.lat : 40.7128,
      lng: typeof validCenter.lng === 'number' && !isNaN(validCenter.lng) ? validCenter.lng : -74.0060
    })
  }, [initialCenter, location])

  const handleAddCircle = (latlng?: LatLng) => {
    if (mode === 'circle') {
      // If no latlng provided, use map center
      if (!latlng && map) {
        const center = map.getCenter()
        setCurrentCircle({ center: { lat: center.lat, lng: center.lng } })
      } else if (latlng) {
        setCurrentCircle({ center: { lat: latlng.lat, lng: latlng.lng } })
      }
    }
  }

  const handleAddPolygonPoint = (latlng: LatLng) => {
    if (mode === 'polygon') {
      setCurrentPolygonPoints([...currentPolygonPoints, { lat: latlng.lat, lng: latlng.lng }])
    }
  }

  const saveCircle = () => {
    if (!currentCircle) {
      alert('Please place a circle on the map first')
      return
    }
    
    if (!geofenceName) {
      alert('Please provide a name for the geofence')
      return
    }

    const newGeofence: Geofence = {
      id: generateId(),
      type: 'CIRCLE',
      name: geofenceName,
      center: currentCircle.center,
      radius: circleRadius
    }

    onGeofencesChange([...geofences, newGeofence])
    setCurrentCircle(null)
    setGeofenceName('')
    setCircleRadius(100)
    // Don't automatically switch to view mode - allow adding more geofences
    // setMode('view')
  }

  const savePolygon = () => {
    if (currentPolygonPoints.length < 3) {
      alert('A polygon must have at least 3 points')
      return
    }

    if (!geofenceName) {
      alert('Please provide a name for the geofence')
      return
    }

    const newGeofence: Geofence = {
      id: generateId(),
      type: 'POLYGON',
      name: geofenceName,
      points: currentPolygonPoints
    }

    onGeofencesChange([...geofences, newGeofence])
    setCurrentPolygonPoints([])
    setGeofenceName('')
    // Don't automatically switch to view mode - allow adding more geofences
    // setMode('view')
  }

  const deleteGeofence = (id: string) => {
    onGeofencesChange(geofences.filter(g => g.id !== id))
    setSelectedGeofence(null)
  }

  const cancelDrawing = () => {
    setMode('view')
    setCurrentCircle(null)
    setCurrentPolygonPoints([])
    setGeofenceName('')
    setCircleRadius(100)
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={mode === 'circle' ? 'primary' : 'secondary'}
            onClick={() => {
              setMode('circle')
              setCurrentPolygonPoints([])
            }}
            disabled={mode === 'polygon' && currentPolygonPoints.length > 0}
          >
            <CircleIcon className="w-4 h-4 mr-1" />
            Add Circle
          </Button>
          <Button
            size="sm"
            variant={mode === 'polygon' ? 'primary' : 'secondary'}
            onClick={() => {
              setMode('polygon')
              setCurrentCircle(null)
            }}
            disabled={mode === 'circle' && currentCircle !== null}
          >
            <Hexagon className="w-4 h-4 mr-1" />
            Add Polygon
          </Button>
          
          {mode !== 'view' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelDrawing}
            >
              Cancel
            </Button>
          )}
        </div>

        <div className="text-sm text-gray-600">
          {mode === 'circle' && 'Click on the map to place a circle geofence'}
          {mode === 'polygon' && `Click points to draw polygon (${currentPolygonPoints.length} points)`}
          {mode === 'view' && `${geofences.length} geofence${geofences.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Drawing Controls */}
      {(currentCircle || currentPolygonPoints.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Geofence Name
            </label>
            <input
              type="text"
              value={geofenceName}
              onChange={(e) => setGeofenceName(e.target.value)}
              placeholder="e.g., Main Building, Parking Lot"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {currentCircle && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Radius: {circleRadius} meters
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-8">10m</span>
                <input
                  type="range"
                  value={circleRadius}
                  onChange={(e) => setCircleRadius(parseInt(e.target.value))}
                  min="10"
                  max="500"
                  step="10"
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((circleRadius - 10) / (500 - 10)) * 100}%, #e5e7eb ${((circleRadius - 10) / (500 - 10)) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <span className="text-xs text-gray-500 w-12 text-right">500m</span>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            {currentCircle && (
              <Button size="sm" onClick={saveCircle} disabled={!geofenceName}>
                Save Circle
              </Button>
            )}
            {currentPolygonPoints.length >= 3 && (
              <Button size="sm" onClick={savePolygon} disabled={!geofenceName}>
                Save Polygon
              </Button>
            )}
            {currentPolygonPoints.length > 0 && currentPolygonPoints.length < 3 && (
              <span className="text-sm text-gray-500">
                Add {3 - currentPolygonPoints.length} more point{3 - currentPolygonPoints.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Map */}
      <div style={{ height }} className="rounded-lg overflow-hidden border border-gray-200">
                  <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={16}
            style={{ height: '100%', width: '100%' }}
            ref={setMap}
          >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          <MapResizer />
          <MapBoundsAdjuster geofences={geofences} />
          <LocationControl 
            onLocationFound={(lat, lng) => {
              setMapCenter({ lat, lng })
              if (onLocationChange) {
                onLocationChange({ lat, lng })
              }
            }}
          />
          {map && (
            <MapInteraction 
              mode={mode} 
              onAddPoint={handleAddPolygonPoint}
              onMapClick={handleAddCircle}
              map={map}
              currentPolygonPoints={currentPolygonPoints}
              currentCircle={currentCircle}
            />
          )}

          {/* Location marker */}
          {mapCenter && typeof mapCenter.lat === 'number' && !isNaN(mapCenter.lat) && 
           typeof mapCenter.lng === 'number' && !isNaN(mapCenter.lng) && (
            <Marker position={[mapCenter.lat, mapCenter.lng]}>
            </Marker>
          )}

          {/* Existing geofences */}
          {geofences.map((geofence) => (
            <div key={geofence.id}>
              {geofence.type === 'CIRCLE' && geofence.center && (
                <Circle
                  center={[geofence.center.lat, geofence.center.lng]}
                  radius={geofence.radius || 100}
                  pathOptions={{
                    color: selectedGeofence === geofence.id ? '#3b82f6' : '#6b7280',
                    fillColor: selectedGeofence === geofence.id ? '#3b82f6' : '#6b7280',
                    fillOpacity: 0.2,
                    weight: 2
                  }}
                  eventHandlers={{
                    click: () => setSelectedGeofence(geofence.id)
                  }}
                />
              )}
              {geofence.type === 'POLYGON' && geofence.points && (
                <Polygon
                  positions={geofence.points.map(p => [p.lat, p.lng])}
                  pathOptions={{
                    color: selectedGeofence === geofence.id ? '#3b82f6' : '#6b7280',
                    fillColor: selectedGeofence === geofence.id ? '#3b82f6' : '#6b7280',
                    fillOpacity: 0.2,
                    weight: 2
                  }}
                  eventHandlers={{
                    click: () => setSelectedGeofence(geofence.id)
                  }}
                />
              )}
            </div>
          ))}

          {/* Current drawing */}
          {currentCircle && (
            <Circle
              center={[currentCircle.center.lat, currentCircle.center.lng]}
              radius={circleRadius}
              pathOptions={{
                color: '#10b981',
                fillColor: '#10b981',
                fillOpacity: 0.2,
                weight: 2,
                dashArray: '5, 5'
              }}
            />
          )}
          {currentPolygonPoints.length > 0 && (
            <Polygon
              positions={currentPolygonPoints.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: '#10b981',
                fillColor: '#10b981',
                fillOpacity: 0.2,
                weight: 2,
                dashArray: '5, 5'
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* Geofence List */}
      {geofences.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Geofences</h4>
          <div className="space-y-1">
            {geofences.map((geofence) => (
              <div
                key={geofence.id}
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedGeofence === geofence.id ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => setSelectedGeofence(geofence.id)}
              >
                <div className="flex items-center gap-2">
                  {geofence.type === 'CIRCLE' ? (
                    <CircleIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <Hexagon className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="text-sm font-medium">{geofence.name}</span>
                  <span className="text-xs text-gray-500">
                    {geofence.type === 'CIRCLE' ? `${geofence.radius}m radius` : `${geofence.points?.length} points`}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteGeofence(geofence.id)
                  }}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}